import { useState } from "react";
import Papa from "papaparse";
import { importComparatorOffersCsv, useAction } from "wasp/client/operations";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../client/components/ui/dialog";
import { Button } from "../../../client/components/ui/button";
import { useToast } from "../../../client/hooks/use-toast";
import { Upload } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

type ImportResult = {
  created: number;
  updated: number;
  errors: { line: number; message: string }[];
};

export default function CsvImportDialog({ open, onOpenChange, onImported }: Props) {
  const importCsv = useAction(importComparatorOffersCsv);
  const { toast } = useToast();

  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => setRows(res.data),
    });
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = (await importCsv({ rows })) as ImportResult;
      setResult(res);
      if (res.errors.length === 0) {
        toast({ title: `Created ${res.created}, updated ${res.updated}` });
        onImported();
      } else {
        toast({ title: "Import aborted — fix errors", variant: "destructive" });
      }
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setRows([]);
    setFileName("");
    setResult(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import offers from CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent">
              <Upload className="h-4 w-4" />
              Choose CSV file
              <input type="file" accept=".csv" className="hidden" onChange={handleFile} />
            </label>
            {fileName && (
              <p className="text-sm text-muted-foreground">
                {fileName} — <span className="font-mono">{rows.length}</span> rows parsed
              </p>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            <a
              href="/comparateur-propfirms-data.csv"
              download
              className="text-primary underline"
            >
              Download CSV template
            </a>
          </p>

          {result && result.errors.length > 0 && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
              <p className="mb-2 text-sm font-medium text-destructive">
                Nothing was imported — the server aborts on any error.
              </p>
              <ul className="space-y-1 text-sm text-destructive">
                {result.errors.map((err, i) => (
                  <li key={i}>
                    Line {err.line}: {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result && result.errors.length === 0 && (
            <div className="rounded-md border border-primary/50 bg-primary/10 p-3">
              <p className="text-sm font-medium text-primary">
                Created {result.created}, updated {result.updated}.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
            Close
          </Button>
          <Button onClick={handleImport} disabled={importing || rows.length === 0}>
            {importing ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
